/**
 * Mantener apretado (long-press) — el gesto con el que cualquier app mobile
 * entra a "seleccionar varios". Sobre pointer events: anda igual con dedo y
 * con mouse.
 *
 * - `pointerdown` (botón primario) arma un timer de `LONG_PRESS_MS`.
 * - Lo desarman: moverse más de `LONG_PRESS_SLOP_PX` (el dedo está
 *   scrolleando), soltar, o `pointercancel` (el navegador se quedó con el
 *   gesto para hacer scroll).
 * - Al disparar vibra (donde hay vibrador: iOS no lo expone) y se COME el
 *   click que el navegador emite al soltar, para que el mismo gesto no abra
 *   también lo que abre el tap. El "comer" se resetea en el próximo
 *   `pointerdown`: si ese click nunca llega (soltó afuera), no se lleva puesto
 *   el tap siguiente.
 * - Android Chrome dispara `contextmenu` al mantener apretado y, si nadie lo
 *   frena, cancela el puntero — según la versión, antes que nuestro timer. Un
 *   `contextmenu` con el timer armado cuenta como long-press y se frena. El
 *   click derecho del mouse no arma el timer, así que su menú queda intacto.
 *
 * Lo que NO resuelve: el menú nativo de copiar texto de iOS Safari. Eso es CSS
 * del elemento (`-webkit-touch-callout: none` + `user-select: none`).
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react';

export const LONG_PRESS_MS = 450;
export const LONG_PRESS_SLOP_PX = 10;

export interface LongPressHandlers {
  onPointerDown: (e: ReactPointerEvent) => void;
  onPointerMove: (e: ReactPointerEvent) => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
  onClickCapture: (e: ReactMouseEvent) => void;
  onContextMenu: (e: ReactMouseEvent) => void;
}

export function useLongPress(onLongPress: () => void): LongPressHandlers {
  // El callback más reciente, sin re-crear los handlers en cada render.
  const cb = useRef(onLongPress);
  useEffect(() => {
    cb.current = onLongPress;
  }, [onLongPress]);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);

  const disarm = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    origin.current = null;
  }, []);

  const fire = useCallback(() => {
    disarm();
    fired.current = true;
    navigator.vibrate?.(10);
    cb.current();
  }, [disarm]);

  useEffect(() => disarm, [disarm]);

  return useMemo<LongPressHandlers>(
    () => ({
      onPointerDown: (e) => {
        fired.current = false;
        if (e.button !== 0) return;
        disarm();
        origin.current = { x: e.clientX, y: e.clientY };
        timer.current = setTimeout(fire, LONG_PRESS_MS);
      },
      onPointerMove: (e) => {
        const o = origin.current;
        if (!o) return;
        if (Math.hypot(e.clientX - o.x, e.clientY - o.y) > LONG_PRESS_SLOP_PX) disarm();
      },
      onPointerUp: disarm,
      onPointerCancel: disarm,
      onClickCapture: (e) => {
        if (!fired.current) return;
        fired.current = false;
        e.preventDefault();
        e.stopPropagation();
      },
      onContextMenu: (e) => {
        if (timer.current !== null) fire();
        if (fired.current) e.preventDefault();
      },
    }),
    [disarm, fire],
  );
}
